import {
  CreateResourceData,
  GetResourceData,
  UpdateResourceData,
  GetUserData,
  IResourceRepository,
  IUserRepository,
} from '../repository/interfaces';
import { IResourcesUseCase } from './interfaces';

/**
 * Resources Use Case
 * Separate from Curriculum - focuses on resource management
 * Handles resource creation, updates, search, and access control
 */
export class ResourcesUseCase  implements IResourcesUseCase {
  constructor(
    private resourceRepo: IResourceRepository,
    private userRepo: IUserRepository
  ) {
    // Removed BaseUseCase
  }

  /**
   * Create a new resource
   */
  async createResource(
    resourceData: CreateResourceData,
    creatorId: string,
    files?: Array<{
      type: 'thumbnail' | 'video' | 'file';
      file: File;
      metadata?: Record<string, any>;
    }>
  ): Promise<{
    resource: GetResourceData;
    uploadedFiles: Array<{
      type: string;
      url: string;
      metadata?: Record<string, any>;
    }>;
  }> {
    try {
      // Verify creator has permission (check subscription tier or role)
      const creator = await this.userRepo.getUserById(creatorId);
      if (!creator) {
        throw new Error('Creator not found');
      }

      // Check if creator has appropriate permissions
      const allowedTiers = ['professional', 'instructor', 'enterprise'];
      const allowedRoles = ['admin', 'instructor', 'contentCreator'];

      const hasTierPermission = allowedTiers.includes(creator.subscriptionTier || '');
      const hasRolePermission = creator.role?.some(role => allowedRoles.includes(role)) || false;

      if (!hasTierPermission && !hasRolePermission) {
        throw new Error('Your account does not have permission to create resources');
      }

      // Upload files if provided
      const uploadedFiles: Array<{
        type: string;
        url: string;
        metadata?: Record<string, any>;
      }> = [];

      for (const fileInfo of files || []) {
        let url: string;

        try {
          switch (fileInfo.type) {
            case 'thumbnail':
              url = await this.resourceRepo.uploadResourceThumbnail('temp', fileInfo.file);
              break;
            case 'video':
              url = await this.resourceRepo.uploadResourceVideo('temp', fileInfo.file);
              break;
            case 'file':
              url = await this.resourceRepo.uploadResourceFile('temp', fileInfo.file);
              break;
            default:
              throw new Error(`Unsupported file type: ${fileInfo.type}`);
          }

          uploadedFiles.push({
            type: fileInfo.type,
            url,
            metadata: fileInfo.metadata
          });
        } catch (error) {
          this.logError(error, `Failed to upload ${fileInfo.type} file`);
          throw new Error(`Failed to upload ${fileInfo.type} file: ${error}`);
        }
      }

      // Update resource data with uploaded file URLs
      const finalResourceData = { ...resourceData };

      for (const uploadedFile of uploadedFiles) {
        if (uploadedFile.type === 'thumbnail') {
          finalResourceData.thumbnailUrl = uploadedFile.url;
        } else if (uploadedFile.type === 'video' || uploadedFile.type === 'file') {
          finalResourceData.resourceUrl = uploadedFile.url;
        }
      }

      // Set instructorId to creator
      finalResourceData.instructorId = creatorId;

      // Create the resource
      const resource = await this.resourceRepo.createResource(finalResourceData);

      return {
        resource,
        uploadedFiles
      };
    } catch (error) { console.error("Error:", error); throw error; }
  }

  /**
   * Update resource
   */
  async updateResource(
    resourceId: string,
    updates: UpdateResourceData,
    userId: string
  ): Promise<GetResourceData> {
    try {
      // Get existing resource
      const existingResource = await this.resourceRepo.getResourceById(resourceId);
      if (!existingResource) {
        throw new Error('Resource not found');
      }

      // Check ownership or admin permission
      const isOwner = existingResource.instructorId === userId;
      const isAdmin = await this.userRepo.isUserAdmin(userId);

      if (!isOwner && !isAdmin) {
        throw new Error('Unauthorized: You do not have permission to update this resource');
      }

      // Update resource
      const updatedResource = await this.resourceRepo.updateResource(resourceId, {
        ...updates,
        updatedAt: new Date()
      });

      return updatedResource;
    } catch (error) { console.error("Error:", error); throw error; }
  }

  /**
   * Delete resource
   */
  async deleteResource(
    resourceId: string,
    userId: string
  ): Promise<{
    resource: GetResourceData;
    filesDeleted: string[];
  }> {
    try {
      // Get existing resource
      const existingResource = await this.resourceRepo.getResourceById(resourceId);
      if (!existingResource) {
        throw new Error('Resource not found');
      }

      // Check ownership or admin permission
      const isOwner = existingResource.instructorId === userId;
      const isAdmin = await this.userRepo.isUserAdmin(userId);

      if (!isOwner && !isAdmin) {
        throw new Error('Unauthorized: You do not have permission to delete this resource');
      }

      // Track files to delete
      const filesDeleted: string[] = [];

      // Delete associated files
      if (existingResource.thumbnailUrl) {
        try {
          await this.resourceRepo.deleteResourceThumbnail(resourceId);
          filesDeleted.push('thumbnail');
        } catch (error) {
          this.logError(error, 'Failed to delete thumbnail');
        }
      }

      if (existingResource.resourceUrl) {
        try {
          if (existingResource.contentType === 'video') {
            await this.resourceRepo.deleteResourceVideo(resourceId);
          } else {
            await this.resourceRepo.deleteResourceFile(resourceId);
          }
          filesDeleted.push('resource');
        } catch (error) {
          this.logError(error, 'Failed to delete resource file');
        }
      }

      // Delete resource
      await this.resourceRepo.deleteResource(resourceId);

      return {
        resource: existingResource,
        filesDeleted
      };
    } catch (error) { console.error("Error:", error); throw error; }
  }

  /**
   * Get resource with access control
   */
  async getResource(
    resourceId: string,
    userId?: string
  ): Promise<{
    resource: GetResourceData;
    creator: GetUserData;
    canAccess: boolean;
    accessReason?: string;
    viewTracked: boolean;
  }> {
    try {
      // Get resource
      const resource = await this.resourceRepo.getResourceById(resourceId);
      if (!resource) {
        throw new Error('Resource not found');
      }

      // Check if resource is published
      if (!resource.isPublished) {
        const isOwner = resource.instructorId === userId;
        const isAdmin = userId ? await this.userRepo.isUserAdmin(userId) : false;

        if (!isOwner && !isAdmin) {
          throw new Error('Resource is not available');
        }
      }

      // Get creator information
      const creator = await this.userRepo.getUserById(resource.instructorId);
      if (!creator) {
        throw new Error('Resource creator not found');
      }

      // Check access permissions
      let canAccess = true;
      let accessReason: string | undefined;

      if (resource.isPremium && resource.price && resource.price > 0) {
        // Premium resource - check if user has purchased or owns it
        if (!userId) {
          canAccess = false;
          accessReason = 'Authentication required for premium content';
        } else {
          // In a real implementation, you'd check purchase records
          // For now, assume free access to own resources
          const isOwner = resource.instructorId === userId;
          const isAdmin = await this.userRepo.isUserAdmin(userId);

          if (!isOwner && !isAdmin) {
            canAccess = false;
            accessReason = 'Purchase required for premium content';
          }
        }
      }

      // Track view if user can access
      let viewTracked = false;
      if (canAccess && userId) {
        try {
          await this.resourceRepo.recordResourceView({
            userId,
            resourceId,
            timeSpentSeconds: 0
          });
          viewTracked = true;
        } catch (error) {
          this.logError(error, 'Failed to track resource view');
        }
      }

      return {
        resource,
        creator,
        canAccess,
        accessReason,
        viewTracked
      };
    } catch (error) { console.error("Error:", error); throw error; }
  }

  /**
   * Search and filter resources
   */
  async searchResources(
    searchCriteria: {
      query?: string;
      contentType?: string;
      difficultyLevel?: string;
      category?: string;
      instructor?: string;
      isPremium?: boolean;
      priceMin?: number;
      priceMax?: number;
      tags?: string[];
      isPublished?: boolean;
    },
    options: {
      sortBy?: 'newest' | 'oldest' | 'priceLow' | 'priceHigh' | 'popular' | 'rating';
      limit?: number;
      offset?: number;
    } = {},
    userId?: string
  ): Promise<{
    resources: Array<GetResourceData & {
      creator: GetUserData;
      canAccess: boolean;
      accessReason?: string;
    }>;
    total: number;
    facets: {
      contentTypes: Array<{ type: string; count: number }>;
      categories: Array<{ category: string; count: number }>;
      difficultyLevels: Array<{ level: string; count: number }>;
      priceRanges: Array<{ range: string; count: number }>;
    };
  }> {
    try {
      // Get all resources (with optional published filter)
      let resources = await this.resourceRepo.getResources({
        isPublished: searchCriteria.isPublished !== false // Default to published only
      });

      // Apply search filters
      if (searchCriteria.query) {
        const query = searchCriteria.query.toLowerCase();
        resources = resources.filter(r =>
          r.title.toLowerCase().includes(query) ||
          r.description?.toLowerCase().includes(query) ||
          r.tags?.some(tag => tag.toLowerCase().includes(query))
        );
      }

      if (searchCriteria.contentType) {
        resources = resources.filter(r => r.contentType === searchCriteria.contentType);
      }

      if (searchCriteria.difficultyLevel) {
        resources = resources.filter(r => r.difficultyLevel === searchCriteria.difficultyLevel);
      }

      if (searchCriteria.instructor) {
        resources = resources.filter(r => r.instructorId === searchCriteria.instructor);
      }

      if (searchCriteria.isPremium !== undefined) {
        resources = resources.filter(r => r.isPremium === searchCriteria.isPremium);
      }

      if (searchCriteria.priceMin !== undefined) {
        resources = resources.filter(r => (r.price || 0) >= searchCriteria.priceMin!);
      }

      if (searchCriteria.priceMax !== undefined) {
        resources = resources.filter(r => (r.price || 0) <= searchCriteria.priceMax!);
      }

      // Sort results
      switch (options.sortBy) {
        case 'newest':
          resources.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
          break;
        case 'oldest':
          resources.sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());
          break;
        case 'priceLow':
          resources.sort((a, b) => (a.price || 0) - (b.price || 0));
          break;
        case 'priceHigh':
          resources.sort((a, b) => (b.price || 0) - (a.price || 0));
          break;
        case 'popular':
        default:
          // Sort by some popularity metric (simplified)
          resources.sort(() => Math.random() - 0.5);
          break;
      }

      // Apply pagination
      const total = resources.length;
      const start = options.offset || 0;
      const limit = options.limit || 20;
      resources = resources.slice(start, start + limit);

      // Get creator information and access control for each resource
      const resourcesWithDetails = await Promise.all(
        resources.map(async (resource) => {
          const creator = await this.userRepo.getUserById(resource.instructorId);

          // Check access (simplified)
          let canAccess = !resource.isPremium || (resource.price || 0) === 0;
          let accessReason: string | undefined;

          if (resource.isPremium && !canAccess) {
            if (!userId) {
              accessReason = 'Authentication required';
            } else {
              // In a real implementation, check purchase records
              const isOwner = resource.instructorId === userId;
              const isAdmin = await this.userRepo.isUserAdmin(userId);
              canAccess = isOwner || isAdmin;
              if (!canAccess) {
                accessReason = 'Purchase required';
              }
            }
          }

          return {
            ...resource,
            creator: creator!,
            canAccess,
            accessReason
          };
        })
      );

      // Generate facets (simplified)
      const allResources = await this.resourceRepo.getResources();
      const contentTypes = this.generateFacet(allResources, 'contentType');
      const categories = this.generateFacet(allResources, 'categoryIds');
      const difficultyLevels = this.generateFacet(allResources, 'difficultyLevel');

      const priceRanges = [
        { range: 'Free', min: 0, max: 0 },
        { range: '$1 - $10', min: 1, max: 10 },
        { range: '$11 - $50', min: 11, max: 50 },
        { range: '$51 - $100', min: 51, max: 100 },
        { range: '$100+', min: 101, max: Infinity }
      ].map(range => ({
        range: range.range,
        count: allResources.filter(r =>
          (r.price || 0) >= range.min && (r.price || 0) <= range.max
        ).length
      }));

      return {
        resources: resourcesWithDetails,
        total,
        facets: {
          contentTypes,
          categories,
          difficultyLevels,
          priceRanges
        }
      };
    } catch (error) { console.error("Error:", error); throw error; }
  }

  /**
   * Get user's created resources
   */
  async getUserResources(
    userId: string,
    filters: {
      isPublished?: boolean;
      contentType?: string;
      limit?: number;
    } = {}
  ): Promise<{
    resources: GetResourceData[];
    stats: {
      total: number;
      published: number;
      draft: number;
      premium: number;
      totalViews: number;
      totalDownloads: number;
    };
  }> {
    try {
      // Get all resources by this user
      const allResources = await this.resourceRepo.getResources();
      const userResources = allResources.filter(r => r.instructorId === userId);

      // Apply filters
      let filteredResources = userResources;

      if (filters.isPublished !== undefined) {
        filteredResources = filteredResources.filter(r => r.isPublished === filters.isPublished);
      }

      if (filters.contentType) {
        filteredResources = filteredResources.filter(r => r.contentType === filters.contentType);
      }

      if (filters.limit) {
        filteredResources = filteredResources.slice(0, filters.limit);
      }

      // Sort by creation date (newest first)
      filteredResources.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());

      // Calculate stats (simplified - would use actual analytics)
      const stats = {
        total: userResources.length,
        published: userResources.filter(r => r.isPublished).length,
        draft: userResources.filter(r => !r.isPublished).length,
        premium: userResources.filter(r => r.isPremium).length,
        totalViews: userResources.length * Math.floor(Math.random() * 100), // Mock
        totalDownloads: userResources.length * Math.floor(Math.random() * 50) // Mock
      };

      return {
        resources: filteredResources,
        stats
      };
    } catch (error) { console.error("Error:", error); throw error; }
  }

  /**
   * Bulk update resources
   */
  async bulkUpdateResources(
    updates: Array<{
      resourceId: string;
      updates: UpdateResourceData;
    }>,
    userId: string
  ): Promise<{
    updated: GetResourceData[];
    failed: Array<{
      resourceId: string;
      error: string;
    }>;
  }> {
    try {
      const updated: GetResourceData[] = [];
      const failed: Array<{ resourceId: string; error: string }> = [];

      for (const update of updates) {
        try {
          const resource = await this.updateResource(update.resourceId, update.updates, userId);
          updated.push(resource);
        } catch (error) {
          failed.push({
            resourceId: update.resourceId,
            error: error instanceof Error ? error.message : 'Unknown error'
          });
        }
      }

      return {
        updated,
        failed
      };
    } catch (error) { console.error("Error:", error); throw error; }
  }

  // Helper method for generating facets
  private generateFacet(resources: GetResourceData[], field: keyof GetResourceData): Array<{ [key: string]: number }> {
    const facetMap = new Map<string, number>();

    resources.forEach(resource => {
      const value = resource[field];
      if (value) {
        if (Array.isArray(value)) {
          value.forEach(item => {
            facetMap.set(item, (facetMap.get(item) || 0) + 1);
          });
        } else {
          facetMap.set(value as string, (facetMap.get(value as string) || 0) + 1);
        }
      }
    });

    return Array.from(facetMap.entries()).map(([key, count]) => ({ [field]: key, count }));
  }
}
